// StoreFrontPage.js
import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useParams, useLocation, useSearchParams, useNavigate } from 'react-router-dom';
import { businessService } from '../../services/api/businessService';
import { trackEvent } from '../../services/api/analyticsService';
import { serviceWizardSearchRequestService } from '../../services/api/serviceWizardSearchRequestService';
import { persistActiveWizardSearchRequest } from '../../utils/persistActiveWizardSearchRequest';
import BackButton from '../common/BackButton';
import ServiceCard from '../ServiceCard/ServiceCard';
import CompactBusinessRatingLine from '../common/CompactBusinessRatingLine/CompactBusinessRatingLine';
import {
  SectionTitle,
  SectionSubtitle,
  HeroSection,
  HeroContent,
  BusinessPhoto,
  BusinessInfo,
  HeroDetailsRow,
  BusinessTitle,
  BusinessLocation,
  InfoItem,
  ContactLink,
  ServicesSection,
  TradeJumpChips,
  TradeJumpChip,
  TradeSection,
  TradeSectionHeader,
  TradeSectionHeaderText,
  TradeSectionIcon,
  TradeSectionTitle,
  TradeSectionCount,
  TradeSectionToggle,
  TradeSectionBody,
  ServiceGroupBlock,
  ServiceGroupLabel,
  ServiceCardsGrid,
  TitleRow,
  StorefrontRatingsColumn,
  StorefrontRatingsActions,
  StorefrontEmptyRatingText,
  ReviewCTACard,
  ReviewCTAText,
  ReviewCTAButton,
  HeroReviewButton,
  EmptyServices,
  FloatingReviewButton,
  StyledExistingReviewDialog,
  ExistingReviewDialogTitle,
  ExistingReviewCloseButton,
  ExistingReviewDialogContent,
  ExistingReviewPanel,
  ExistingReviewMetaRow,
  ExistingReviewDialogActions,
  ExistingReviewPrimaryButton,
  ExistingReviewSecondaryButton,
  ExistingReviewRejectionNotice,
} from './StoreFrontPage.styled';
import {
  Typography,
  Alert,
  Box,
  Collapse,
} from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import HomeIcon from '@mui/icons-material/Home';
import PhoneIcon from '@mui/icons-material/Phone';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { reviewsService } from '../../services/api/reviewsService';
import CustomerReviews from '../CustomerReviews/CustomerReviews';
import { Rating } from '@mui/material';
import ReviewsForm from '../ReviewsForm/ReviewsForm';
import { Dialog, DialogTitle, DialogContent, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import RateReviewOutlinedIcon from '@mui/icons-material/RateReviewOutlined';
import NkySpinner from '../common/NkySpinner/NkySpinner';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';
import { useAuth } from '../../context/AuthContext';
import AuthModal from '../common/AuthModal/AuthModal';
import {
  flattenAndEnrichBusinessServices,
  groupStorefrontServicesByTradeAndGroup,
  storefrontTradeSectionId,
} from '../../utils/storefrontServiceDisplay';
import { getWizardUrgencyLabel } from '../../constants/wizardUrgencyOptions';
import ContactForServiceModal from '../common/ContactForServiceModal/ContactForServiceModal';
import CustomServiceInquiryModal from '../common/CustomServiceInquiryModal/CustomServiceInquiryModal';
import InquirySentToast from '../common/InquirySentToast/InquirySentToast';
import {
  fingerprintWizardState,
  getPersistedWizardSearchRequestId,
  getLastWizardSearchRequestId,
  getOrCreateWizardClientSessionId,
} from '../../utils/wizardClientSession';
import { resolveServiceTypeDisplayLabel } from '../../utils/serviceTypeDisplay';
import { getSubserviceDisplayLabel } from '../../utils/getSubserviceDisplayLabel';
import { consumeSendInquiryResume } from '../../utils/sendInquiryResume';
import { useWizardStateFromSearchRequestApi } from '../../hooks/useWizardStateFromSearchRequestApi';
import { storefrontInquiryMatchesWizardSearch } from '../../utils/storefrontInquiryWizardMatch';
import { isStorefrontInquiryAlreadySent } from '../../utils/customServiceInquirySent';
import { resolveSubserviceTitle } from '../../utils/subserviceDisplay';
import AlreadySentInquiryModal from '../common/AlreadySentInquiryModal/AlreadySentInquiryModal';
import { buildGoogleMapsSearchUrl } from '../../utils/googleMapsUrl';
import SEO from '../common/SEO/SEO';
import { buildStorefrontSeo } from '../common/SEO/seoHelpers';

const StoreFrontPage = () => {
  const { businessSlug } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [provider, setProvider] = useState(null);
  const resolvedBusinessId = provider?.BusinessId ?? null;
  const [customServices, setCustomServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reviews, SetReviews] = useState([]);
  const reviewsRef = useRef(null);
  const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);
  const { user, loading: authLoading } = useAuth();
  const [showAuthDialog, setShowAuthDialog] = useState(false);
  const [existingReviewDialogOpen, setExistingReviewDialogOpen] = useState(false);
  const [existingReview, setExistingReview] = useState(null);
  const [contactForServiceOpen, setContactForServiceOpen] = useState(false);
  const [contactSuccessOpen, setContactSuccessOpen] = useState(false);
  const [resumeLinkClientSessionId, setResumeLinkClientSessionId] = useState(null);
  const [pendingInquiryResume, setPendingInquiryResume] = useState(false);
  /**
   * Catalog card inquiry without a matching saved search: the contact sheet asks timing + note
   * and saves the request itself.
   */
  const [inquiryDraft, setInquiryDraft] = useState(null);
  const [customInquiryOpen, setCustomInquiryOpen] = useState(false);
  const [customInquiryService, setCustomInquiryService] = useState(null);
  const [customInquiryRequestId, setCustomInquiryRequestId] = useState(null);
  const [viewerInquiryRequests, setViewerInquiryRequests] = useState([]);
  const [alreadySentInquiryModal, setAlreadySentInquiryModal] = useState({
    open: false,
    serviceName: '',
  });
  /** Trade section ids the user has collapsed (all expanded by default). */
  const [collapsedTradeIds, setCollapsedTradeIds] = useState(() => new Set());
  /** Jump-chip target: expand first, then scroll after the trade is open. */
  const pendingScrollTradeIdRef = useRef(null);

  const staticWizardSearchRequestId = useMemo(() => {
    const fromState = location.state?.wizardSearchRequestId;
    if (fromState) return fromState;
    const q = searchParams.get('wizardRequestId');
    if (q) return q;
    const w = location.state?.wizard;
    const fp = fingerprintWizardState(w);
    const match = getPersistedWizardSearchRequestId(fp);
    if (match) return match;
    if (w) return null;
    return getLastWizardSearchRequestId();
  }, [location.state, searchParams]);

  const [storefrontCreatedRequestId, setStorefrontCreatedRequestId] = useState(null);
  const persistStorefrontWizardRef = useRef(false);
  const pendingInquiryServiceAfterHydrateRef = useRef(null);

  const wizardSearchRequestId =
    customInquiryRequestId ?? storefrontCreatedRequestId ?? staticWizardSearchRequestId;

  const clientSessionIdForRequests = useMemo(() => {
    if (user) return null;
    try {
      return getOrCreateWizardClientSessionId();
    } catch {
      return null;
    }
  }, [user]);

  const routerWizard = location.state?.wizard;
  /** Unsaved service search answers from the results page ("View profile"). */
  const routerServiceSearch = location.state?.serviceSearch ?? null;
  const { wizardState: wizardFromApi, loading: wizardSearchHydrating } =
    useWizardStateFromSearchRequestApi(
      Boolean(user),
      wizardSearchRequestId,
      routerWizard,
      { linkClientSessionId: resumeLinkClientSessionId },
    );
  const effectiveWizard = routerWizard ?? wizardFromApi;

  const wizardFingerprint = useMemo(
    () => fingerprintWizardState(effectiveWizard),
    [effectiveWizard],
  );

  const refreshViewerInquiryRequests = useCallback(async () => {
    if (!resolvedBusinessId) return;
    try {
      let rows = [];
      if (user) {
        rows = await serviceWizardSearchRequestService.listByAccount();
      } else if (clientSessionIdForRequests) {
        rows = await serviceWizardSearchRequestService.listByClientSessionId(
          clientSessionIdForRequests,
        );
      }
      setViewerInquiryRequests(Array.isArray(rows) ? rows : []);
    } catch (err) {
      console.error('Failed to load inquiry history for storefront:', err);
    }
  }, [resolvedBusinessId, user, clientSessionIdForRequests]);

  useEffect(() => {
    refreshViewerInquiryRequests();
  }, [refreshViewerInquiryRequests]);

  useEffect(() => {
    persistStorefrontWizardRef.current = false;
  }, [wizardFingerprint]);

  useEffect(() => {
    const w = effectiveWizard;
    if (!w?.formData || !w.serviceType) {
      setStorefrontCreatedRequestId(null);
      return undefined;
    }
    const fp = fingerprintWizardState(w);
    const existing = getPersistedWizardSearchRequestId(fp);
    if (existing) {
      setStorefrontCreatedRequestId(existing);
      return undefined;
    }
    if (persistStorefrontWizardRef.current) return undefined;
    persistStorefrontWizardRef.current = true;

    let cancelled = false;
    (async () => {
      try {
        const id = await persistActiveWizardSearchRequest(w);
        if (cancelled) return;
        if (id) setStorefrontCreatedRequestId(id);
      } catch (err) {
        console.error('Storefront wizard search persist failed:', err);
        persistStorefrontWizardRef.current = false;
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [effectiveWizard]);

  const contactModalSearchLabels = useMemo(() => {
    const w = effectiveWizard;
    if (!w) {
      return {
        serviceLabel: undefined,
        subserviceLabel: undefined,
        urgencyLabel: undefined,
      };
    }
    const serviceLabel = w.serviceType
      ? resolveServiceTypeDisplayLabel(w.serviceType, { inquiryKind: w.inquiryKind })
      : undefined;
    const subserviceLabel =
      w.inquiryKind === 'custom'
        ? w.targetServiceTitle || w.subServiceType
        : w.subServiceType
          ? getSubserviceDisplayLabel(w.subServiceType)
          : undefined;
    const urgencyLabel =
      w.requestSummary?.urgency ||
      (w.formData?.urgency ? getWizardUrgencyLabel(w.formData.urgency) : undefined);
    const locationLabel =
      w.requestSummary?.location ||
      (w.formData?.city?.trim()
        ? `${w.formData.city.trim()}${w.formData.state ? `, ${w.formData.state}` : ''}`
        : undefined);
    return { serviceLabel, subserviceLabel, urgencyLabel, locationLabel };
  }, [effectiveWizard]);

  useEffect(() => {
    if (!user || !resolvedBusinessId) return;
    const data = consumeSendInquiryResume();
    if (!data) return;
    if (data.businessId && data.businessId !== resolvedBusinessId) return;
    if (data.wizardSearchRequestId) {
      setCustomInquiryRequestId(data.wizardSearchRequestId);
    }
    if (data.clientSessionId) {
      setResumeLinkClientSessionId(data.clientSessionId);
    }
    setPendingInquiryResume(true);
  }, [user, resolvedBusinessId]);

  useEffect(() => {
    if (!pendingInquiryResume || !effectiveWizard?.formData) return;
    setContactForServiceOpen(true);
    setPendingInquiryResume(false);
    setResumeLinkClientSessionId(null);
  }, [pendingInquiryResume, effectiveWizard]);

  const openReviewModal = () => setIsReviewModalOpen(true);
  const closeReviewModal = () => setIsReviewModalOpen(false);
  const closeExistingReviewDialog = () => {
    setExistingReviewDialogOpen(false);
    setExistingReview(null);
  };
  const goToContactAboutRemovedReview = () => {
    closeExistingReviewDialog();
    navigate('/contact');
  };
  const reviewRemovedByAdmins = existingReview?.IsPublished === false;
  const refreshStorefrontData = useCallback(
    async ({ initialLoad = false } = {}) => {
      if (initialLoad) setLoading(true);
      try {
        const data = await businessService.getBusiness(businessSlug, true);
        const reviewData = await reviewsService.getReviews(data.BusinessId, true);
        setProvider(data);
        setCustomServices(flattenAndEnrichBusinessServices(data.Services));
        SetReviews(reviewData);
        setError(null);
      } catch (err) {
        setError('Failed to load business details.');
      } finally {
        if (initialLoad) setLoading(false);
      }
    },
    [businessSlug],
  );

  useEffect(() => {
    refreshStorefrontData({ initialLoad: true });
  }, [refreshStorefrontData]);


  const handleCloseAuthModal = () => setShowAuthDialog(false);


  const handleWriteReviewClick = async () => {
    if (authLoading) return;
    if (!user) {
      setShowAuthDialog(true);
      return;
    }
    try {
      const mine = await reviewsService.getMyReview(resolvedBusinessId);
      if (mine) {
        setExistingReview(mine);
        setExistingReviewDialogOpen(true);
        return;
      }
    } catch {
      // Fall back to opening the form if check fails.
    }
    openReviewModal();
  };

  const scrollToReviews = () => {
    reviewsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };


  const trackStorefrontContactLead = (eventType) => {
    if (!resolvedBusinessId) return;
    trackEvent({ eventType, businessId: resolvedBusinessId });
  };

  const formatPhoneNumber = (number) => {
    if (!number) return '';
    const cleaned = ('' + number).replace(/\D/g, '');
    const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
    return match ? `${match[1]}-${match[2]}-${match[3]}` : number;
  };

  /** Digits-only href for tel: (US 10-digit → +1…) */
  const getTelHref = (raw) => {
    const digits = String(raw ?? '').replace(/\D/g, '');
    if (!digits) return null;
    if (digits.length === 10) return `tel:+1${digits}`;
    if (digits.length === 11 && digits.startsWith('1')) return `tel:+${digits}`;
    return `tel:+${digits}`;
  };

  const hasInquirySearchContext = Boolean(
    wizardSearchRequestId &&
      effectiveWizard?.formData &&
      effectiveWizard?.serviceType,
  );

  const openAlreadySentInquiryModal = useCallback((service) => {
    const serviceType = service?._parentServiceType || service?.ServiceType;
    const serviceName = resolveSubserviceTitle(serviceType, service);
    setAlreadySentInquiryModal({ open: true, serviceName });
  }, []);

  const closeAlreadySentInquiryModal = useCallback(() => {
    setAlreadySentInquiryModal({ open: false, serviceName: '' });
  }, []);

  const handleSendInquiryForCard = useCallback(
    (service) => {
      if (
        resolvedBusinessId &&
        isStorefrontInquiryAlreadySent(viewerInquiryRequests, {
          businessId: resolvedBusinessId,
          service,
          wizardSearchRequestId,
        })
      ) {
        openAlreadySentInquiryModal(service);
        return;
      }
      if (service?.IsCustom) {
        setCustomInquiryService(service);
        setCustomInquiryOpen(true);
        return;
      }
      const serviceType = service?._parentServiceType || service?.ServiceType;
      if (routerServiceSearch && serviceType) {
        // Coming from search results: start the contact sheet from those answers for this card.
        const sameTrade = routerServiceSearch.serviceType === serviceType;
        setInquiryDraft({
          serviceType,
          subServiceType: service.SubServiceType != null ? String(service.SubServiceType) : null,
          ...(sameTrade
            ? { timing: routerServiceSearch.timing ?? null, note: routerServiceSearch.note || '' }
            : {}),
        });
        setContactForServiceOpen(true);
        return;
      }
      if (
        wizardSearchRequestId &&
        wizardSearchHydrating &&
        !hasInquirySearchContext
      ) {
        pendingInquiryServiceAfterHydrateRef.current = service;
        return;
      }
      if (
        hasInquirySearchContext &&
        storefrontInquiryMatchesWizardSearch(effectiveWizard, service)
      ) {
        setInquiryDraft(null);
        setContactForServiceOpen(true);
        return;
      }
      if (!serviceType) return;
      setInquiryDraft({
        serviceType,
        subServiceType: service.SubServiceType != null ? String(service.SubServiceType) : null,
      });
      setContactForServiceOpen(true);
    },
    [
      hasInquirySearchContext,
      effectiveWizard,
      routerServiceSearch,
      resolvedBusinessId,
      wizardSearchRequestId,
      wizardSearchHydrating,
      viewerInquiryRequests,
      openAlreadySentInquiryModal,
    ],
  );

  useEffect(() => {
    const pending = pendingInquiryServiceAfterHydrateRef.current;
    if (!pending || wizardSearchHydrating) return;
    pendingInquiryServiceAfterHydrateRef.current = null;
    handleSendInquiryForCard(pending);
  }, [wizardSearchHydrating, handleSendInquiryForCard]);

  const groupedServices = useMemo(
    () => groupStorefrontServicesByTradeAndGroup(customServices),
    [customServices],
  );

  const toggleTradeExpanded = useCallback((tradeId) => {
    setCollapsedTradeIds((prev) => {
      const next = new Set(prev);
      if (next.has(tradeId)) next.delete(tradeId);
      else next.add(tradeId);
      return next;
    });
  }, []);

  const scrollToTradeSection = useCallback((tradeId) => {
    const scrollNow = () => {
      const el = document.getElementById(storefrontTradeSectionId(tradeId));
      el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    setCollapsedTradeIds((prev) => {
      if (!prev.has(tradeId)) {
        // Already open — scroll on the next frame.
        requestAnimationFrame(scrollNow);
        return prev;
      }
      // Expand, then scroll after React commits (see effect below).
      pendingScrollTradeIdRef.current = tradeId;
      const next = new Set(prev);
      next.delete(tradeId);
      return next;
    });
  }, []);

  useEffect(() => {
    const tradeId = pendingScrollTradeIdRef.current;
    if (!tradeId || collapsedTradeIds.has(tradeId)) return;
    pendingScrollTradeIdRef.current = null;
    // Let Collapse start opening so the section has meaningful height.
    const timeoutId = window.setTimeout(() => {
      const el = document.getElementById(storefrontTradeSectionId(tradeId));
      el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 40);
    return () => window.clearTimeout(timeoutId);
  }, [collapsedTradeIds]);

  if (loading) {
    return (
      <>
        <SEO
          title="Loading business"
          description="Loading business listing on NKY Connect."
          path={`/storefront/${businessSlug || ''}`}
        />
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '60vh',
          }}
        >
          <NkySpinner />
        </Box>
      </>
    );
  }
  if (error) {
    return (
      <>
        <SEO {...buildStorefrontSeo({ notFound: true })} />
        <Alert severity="error">{error}</Alert>
      </>
    );
  }
  if (!provider) {
    return (
      <>
        <SEO {...buildStorefrontSeo({ notFound: true })} />
        <Alert severity="warning">No business found.</Alert>
      </>
    );
  }

  const storefrontSeo = buildStorefrontSeo({ business: provider });
  const storefrontReviewCount = Number(provider.ReviewCount ?? 0);
  const storefrontHasReviews =
    storefrontReviewCount > 0 &&
    provider.AverageRating != null &&
    !Number.isNaN(Number(provider.AverageRating));

  const googleReview = provider.GoogleReview;
  const googleReviewCount = Number(googleReview?.ReviewCount ?? 0);
  const storefrontHasGoogleReview =
    googleReview?.Rating != null &&
    !Number.isNaN(Number(googleReview.Rating)) &&
    googleReviewCount > 0;

  const storefrontEmail = (provider.BusinessEmail && String(provider.BusinessEmail).trim()) || '';
  const storefrontPhoneDisplay = formatPhoneNumber(provider.BusinessPhoneNumber);
  const storefrontTelHref = getTelHref(provider.BusinessPhoneNumber);
  const buAddr = provider.BusinessAddress;
  const storefrontAddressIsPublic = buAddr?.ShowAddressToPublic !== false;
  const storefrontMapsUrl = storefrontAddressIsPublic
    ? buildGoogleMapsSearchUrl(provider.BusinessAddress)
    : null;
  const storefrontStreetLine = storefrontAddressIsPublic
    ? [buAddr?.AddressLine1, buAddr?.AddressLine2]
        .map((x) => (x == null ? '' : String(x).trim()))
        .filter(Boolean)
        .join(', ')
    : '';
  const storefrontCityState = [buAddr?.City, buAddr?.State]
    .map((x) => (x == null ? '' : String(x).trim()))
    .filter(Boolean)
    .join(', ');

  return (
    <>
      <SEO {...storefrontSeo} />
      {/* Hero Section */}
      <HeroSection>
        <HeroContent>
          {provider.BusinessPhotoUrl && (
            <BusinessPhoto>
              <img
                src={provider.BusinessPhotoUrl}
                alt={`${provider.BusinessName}`}
              />
            </BusinessPhoto>
          )}

          <BusinessInfo>
            <TitleRow>
              <div>
                <BusinessTitle>{provider.BusinessName}</BusinessTitle>
                {storefrontCityState ? (
                  <BusinessLocation>
                    <LocationOnIcon fontSize="small" />
                    {storefrontCityState}
                  </BusinessLocation>
                ) : null}
              </div>

              <StorefrontRatingsActions>
                <StorefrontRatingsColumn>
                  {storefrontHasReviews ? (
                    <CompactBusinessRatingLine
                      source="nky"
                      surface="hero"
                      as="button"
                      rating={provider.AverageRating}
                      reviewCount={storefrontReviewCount}
                      onClick={scrollToReviews}
                    />
                  ) : (
                    <StorefrontEmptyRatingText>
                      No reviews on NKY Connect yet
                    </StorefrontEmptyRatingText>
                  )}

                  {storefrontHasGoogleReview && (
                    <CompactBusinessRatingLine
                      source="google"
                      surface="hero"
                      rating={googleReview.Rating}
                      reviewCount={googleReviewCount}
                    />
                  )}
                </StorefrontRatingsColumn>

                <HeroReviewButton type="button" onClick={handleWriteReviewClick}>
                  Leave a review
                </HeroReviewButton>
              </StorefrontRatingsActions>
            </TitleRow>

            <HeroDetailsRow>
              {storefrontAddressIsPublic && storefrontStreetLine ? (
                <InfoItem>
                  <HomeIcon aria-hidden />
                  {storefrontMapsUrl ? (
                    <ContactLink
                      href={storefrontMapsUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="Open street address in Google Maps"
                    >
                      {storefrontStreetLine}
                    </ContactLink>
                  ) : (
                    storefrontStreetLine
                  )}
                </InfoItem>
              ) : null}
              {storefrontEmail ? (
                <InfoItem>
                  <EmailIcon aria-hidden />
                  <ContactLink
                    href={`mailto:${storefrontEmail}`}
                    onClick={() => trackStorefrontContactLead('email_click')}
                  >
                    {storefrontEmail}
                  </ContactLink>
                </InfoItem>
              ) : null}
              {storefrontPhoneDisplay ? (
                <InfoItem>
                  <PhoneIcon aria-hidden />
                  {storefrontTelHref ? (
                    <ContactLink
                      href={storefrontTelHref}
                      onClick={() => trackStorefrontContactLead('phone_click')}
                    >
                      {storefrontPhoneDisplay}
                    </ContactLink>
                  ) : (
                    storefrontPhoneDisplay
                  )}
                </InfoItem>
              ) : null}
            </HeroDetailsRow>
          </BusinessInfo>
        </HeroContent>
      </HeroSection>

      {/* Service section */}
      <ServicesSection aria-labelledby="storefront-services-title">
        <SectionTitle id="storefront-services-title">
          Services from {provider.BusinessName}
        </SectionTitle>
        <SectionSubtitle>
          Browse their offerings and send an inquiry directly to the business.
        </SectionSubtitle>

        {customServices.length > 0 ? (
          <>
            {groupedServices.length > 1 && (
              <TradeJumpChips role="navigation" aria-label="Jump to trade">
                {groupedServices.map((trade) => {
                  const TradeIcon = trade.icon;
                  return (
                    <TradeJumpChip
                      key={trade.tradeId}
                      type="button"
                      $accent={trade.tradeAccentColor}
                      onClick={() => scrollToTradeSection(trade.tradeId)}
                    >
                      {TradeIcon ? (
                        <span className="trade-jump-icon" aria-hidden>
                          <TradeIcon />
                        </span>
                      ) : null}
                      {trade.tradeLabel}
                    </TradeJumpChip>
                  );
                })}
              </TradeJumpChips>
            )}

            {groupedServices.map((trade) => {
              const TradeIcon = trade.icon;
              const isExpanded = !collapsedTradeIds.has(trade.tradeId);
              const panelId = `${storefrontTradeSectionId(trade.tradeId)}-panel`;
              return (
                <TradeSection
                  key={trade.tradeId}
                  id={storefrontTradeSectionId(trade.tradeId)}
                >
                  <TradeSectionHeader
                    type="button"
                    $accent={trade.tradeAccentColor}
                    aria-expanded={isExpanded}
                    aria-controls={panelId}
                    onClick={() => toggleTradeExpanded(trade.tradeId)}
                  >
                    {TradeIcon ? (
                      <TradeSectionIcon $accent={trade.tradeAccentColor} aria-hidden>
                        <TradeIcon />
                      </TradeSectionIcon>
                    ) : null}
                    <TradeSectionHeaderText>
                      <TradeSectionTitle>{trade.tradeLabel}</TradeSectionTitle>
                      <TradeSectionCount>
                        {trade.serviceCount}{' '}
                        {trade.serviceCount === 1 ? 'service' : 'services'}
                      </TradeSectionCount>
                    </TradeSectionHeaderText>
                    <TradeSectionToggle $expanded={isExpanded} aria-hidden>
                      <ExpandMoreIcon />
                    </TradeSectionToggle>
                  </TradeSectionHeader>

                  <Collapse in={isExpanded} timeout="auto" unmountOnExit={false}>
                    <TradeSectionBody id={panelId} role="region">
                      {trade.groups.map((group, groupIndex) => (
                        <ServiceGroupBlock
                          key={group.groupId || `ungrouped-${groupIndex}`}
                        >
                          {group.groupName ? (
                            <ServiceGroupLabel>{group.groupName}</ServiceGroupLabel>
                          ) : null}
                          <ServiceCardsGrid>
                            {group.services.map((service) => (
                              <ServiceCard
                                key={`${service._parentServiceType || 'x'}-${
                                  service.SubServiceType || 'sub'
                                }`}
                                service={service}
                                showBusinessHeader={false}
                                onBookService={handleSendInquiryForCard}
                                onRequestQuote={handleSendInquiryForCard}
                                onWizardSearchInquiry={handleSendInquiryForCard}
                              />
                            ))}
                          </ServiceCardsGrid>
                        </ServiceGroupBlock>
                      ))}
                    </TradeSectionBody>
                  </Collapse>
                </TradeSection>
              );
            })}
          </>
        ) : (
          <EmptyServices>
            <h3>No services listed yet</h3>
            <p>This business hasn't added any services yet. Check back later.</p>
          </EmptyServices>
        )}
      </ServicesSection>

      {/* Review CTA */}
      <ReviewCTACard>
        <ReviewCTAText>
          <h2>Had a good experience?</h2>
          <p>Help others in NKY find great service. Leave a quick review.</p>
        </ReviewCTAText>
        <ReviewCTAButton type="button" onClick={handleWriteReviewClick}>
          Write a review
        </ReviewCTAButton>
      </ReviewCTACard>

      <div ref={reviewsRef}>
        <CustomerReviews reviews={reviews} title="Reviews on NKY Connect" />
        {user && (
          <Dialog
            open={isReviewModalOpen}
            onClose={closeReviewModal}
            fullWidth
            maxWidth="sm"
          >
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              Leave a review
              <IconButton onClick={closeReviewModal} aria-label="close">
                <CloseIcon />
              </IconButton>
            </DialogTitle>

            <DialogContent dividers>
              <ReviewsForm
                businessId={resolvedBusinessId}
                businessName={provider?.BusinessName}
                onSubmitted={async () => {
                  await refreshStorefrontData();
                  closeReviewModal();
                  setTimeout(scrollToReviews, 250);
                }}
                onCancel={closeReviewModal}
              />
            </DialogContent>
          </Dialog>
        )}

        <StyledExistingReviewDialog
          open={existingReviewDialogOpen}
          onClose={closeExistingReviewDialog}
          fullWidth
          maxWidth="sm"
          aria-labelledby="existing-review-dialog-title"
        >
          <ExistingReviewDialogTitle id="existing-review-dialog-title">
            <span className="existing-review-icon-wrap" aria-hidden>
              {reviewRemovedByAdmins ? (
                <ReportProblemOutlinedIcon className="existing-review-icon" />
              ) : (
                <RateReviewOutlinedIcon className="existing-review-icon" />
              )}
            </span>
            <div className="existing-review-title-block">
              <p className="existing-review-heading">
                {reviewRemovedByAdmins
                  ? 'Your review was not approved for the storefront'
                  : 'You already wrote a review'}
              </p>
              <p className="existing-review-subheading">
                {reviewRemovedByAdmins
                  ? 'NKY Connect admins removed your review from public view. You can still see what you submitted below.'
                  : 'One review per business. Here is yours.'}
              </p>
            </div>
            <ExistingReviewCloseButton
              onClick={closeExistingReviewDialog}
              aria-label="Close dialog"
            >
              <CloseIcon />
            </ExistingReviewCloseButton>
          </ExistingReviewDialogTitle>
          <ExistingReviewDialogContent>
            {reviewRemovedByAdmins && (
              <ExistingReviewRejectionNotice role="status">
                <Typography
                  component="p"
                  sx={{
                    margin: 0,
                    fontWeight: 700,
                    fontSize: '0.9375rem',
                    color: '#1a1a2e',
                    lineHeight: 1.45,
                  }}
                >
                  This review was rejected by NKY Connect admins
                </Typography>
                <Typography
                  component="p"
                  sx={{
                    margin: 0,
                    marginTop: 1,
                    fontSize: '0.875rem',
                    lineHeight: 1.6,
                    color: '#5a6478',
                  }}
                >
                  It no longer appears on this business&apos;s storefront. That usually
                  means it did not meet our community or quality guidelines. If you believe
                  this was a mistake, reach out via Contact us and our team can look into
                  it.
                </Typography>
              </ExistingReviewRejectionNotice>
            )}
            <ExistingReviewMetaRow>
              <Rating
                value={Number(existingReview?.Rating || 0)}
                precision={0.5}
                readOnly
                sx={{
                  '& .MuiRating-iconFilled': { color: '#0d4efe' },
                  '& .MuiRating-iconHover': { color: '#0d4efe' },
                  '& .MuiRating-iconEmpty .MuiSvgIcon-root': {
                    color: 'rgba(13, 78, 254, 0.22)',
                  },
                }}
              />
              {existingReview?.CreatedDate && (
                <Typography
                  variant="caption"
                  sx={{ color: '#8a93a5', fontWeight: 500, letterSpacing: '0.02em' }}
                >
                  Submitted {new Date(existingReview.CreatedDate).toLocaleDateString()}
                </Typography>
              )}
            </ExistingReviewMetaRow>
            <ExistingReviewPanel>
              <Typography
                sx={{
                  color: '#1a1a2e',
                  whiteSpace: 'pre-line',
                  fontSize: '0.9375rem',
                  lineHeight: 1.65,
                }}
              >
                {existingReview?.ReviewText?.trim() || 'No written review provided.'}
              </Typography>
            </ExistingReviewPanel>
          </ExistingReviewDialogContent>
          <ExistingReviewDialogActions>
            {reviewRemovedByAdmins && (
              <ExistingReviewSecondaryButton
                variant="outlined"
                onClick={goToContactAboutRemovedReview}
              >
                Contact us if this is a mistake
              </ExistingReviewSecondaryButton>
            )}
            <ExistingReviewPrimaryButton onClick={closeExistingReviewDialog}>
              Close
            </ExistingReviewPrimaryButton>
          </ExistingReviewDialogActions>
        </StyledExistingReviewDialog>
      </div>

      {/* Back Button Container */}
      <>
        <Box sx={{ mt: 3, mb: 4 }}>
          <BackButton />
        </Box>
        <AuthModal
          open={showAuthDialog}
          onClose={handleCloseAuthModal}
          title="Account Required"
          message="To leave a review, please log in or create an account."
        />

        <AlreadySentInquiryModal
          open={alreadySentInquiryModal.open}
          onClose={closeAlreadySentInquiryModal}
          businessName={provider?.BusinessName}
          serviceName={alreadySentInquiryModal.serviceName}
        />

        <CustomServiceInquiryModal
          open={customInquiryOpen}
          onClose={() => {
            setCustomInquiryOpen(false);
            setCustomInquiryService(null);
          }}
          service={customInquiryService}
          provider={provider}
          businessId={resolvedBusinessId}
          onSent={({ requestId }) => {
            setCustomInquiryRequestId(requestId);
            setCustomInquiryOpen(false);
            setCustomInquiryService(null);
            setContactSuccessOpen(true);
            refreshViewerInquiryRequests();
          }}
        />

        <ContactForServiceModal
          open={contactForServiceOpen}
          onClose={() => {
            setContactForServiceOpen(false);
            setInquiryDraft(null);
          }}
          businessId={resolvedBusinessId}
          businessName={provider?.BusinessName}
          businessPhone={provider?.BusinessPhoneNumber}
          wizardSearchRequestId={inquiryDraft ? undefined : wizardSearchRequestId}
          draftSearch={inquiryDraft || undefined}
          clientSessionId={clientSessionIdForRequests}
          user={user}
          serviceLabel={contactModalSearchLabels.serviceLabel}
          subserviceLabel={contactModalSearchLabels.subserviceLabel}
          urgencyLabel={contactModalSearchLabels.urgencyLabel}
          wizardState={effectiveWizard}
          onSent={refreshViewerInquiryRequests}
        />

        <InquirySentToast
          open={contactSuccessOpen}
          onClose={() => setContactSuccessOpen(false)}
          user={user}
        />

      </>

      {/* Floating review button */}
      <FloatingReviewButton
        type="button"
        onClick={handleWriteReviewClick}
        aria-label="Leave a review"
      >
        ★ Review
      </FloatingReviewButton>
    </>
  );
};

export default StoreFrontPage;